import { forwardRef, useState, type ReactNode } from "react";
import { cn } from "@/shared/utils/cn";
import { Button } from "../shadcn/Button";
import { FullScreenDialog, type DialogDismissReason } from "./FullScreenDialog";
import { AmountInput } from "./AmountInput";

export interface AmountPickerProps {
  open?: boolean;
  value: string;
  onValueChange: (value: string) => void;
  onAdd?: () => void;
  onClose: (reason?: DialogDismissReason) => void;
  description?: string;
  className?: string;
  title?: string;
  buttonText?: string;
  maxAmount?: number;
  currency?: string;
  renderPreview?: (valueInCents: number, setValue: (cents: string) => void) => ReactNode;
  renderActions?: (setValue: (cents: string) => void) => ReactNode;
}

const AmountEditor = forwardRef<HTMLDivElement, AmountPickerProps>(
  ({
    value,
    onValueChange,
    onAdd,
    onClose,
    description,
    className,
    title = "Enter Amount",
    buttonText = "Add",
    maxAmount = 1000000,
    currency = "USD",
    renderPreview,
    renderActions,
  }, ref) => {
    const limit = Number.isFinite(maxAmount) ? Math.max(0, Math.floor(maxAmount)) : 1000000;
    const normalize = (amount: string) => Math.min(Number(amount.replace(/\D/g, "") || "0"), limit).toString();
    const [localValue, setLocalValue] = useState(() => {
      const initialCents = Number(value);
      return Number.isFinite(initialCents) ? (initialCents / 100).toFixed(2) : value;
    });
    const numericValue = Number(localValue);
    const cents = Number.isFinite(numericValue) ? Math.round(numericValue * 100) : 0;
    const error = !/^(?:\d+(?:\.\d{1,2})?|\.\d{1,2})$/.test(localValue) || !Number.isFinite(numericValue)
      ? "Enter an amount of 0 or more with up to 2 decimal places."
      : cents > limit
        ? `Enter an amount no greater than ${new Intl.NumberFormat(undefined, { style: "currency", currency }).format(limit / 100)}.`
        : undefined;
    const setAmount = (amount: string) => setLocalValue((Number(normalize(amount)) / 100).toFixed(2));

    return (
      <FullScreenDialog
        narrowFooter
        onClose={onClose}
        title={title}
        description={description}
        footer={
          <Button disabled={!!error} onClick={() => { if (error) return; onValueChange(cents.toString()); if (onAdd) onAdd(); else onClose(); }}>
            {buttonText}
          </Button>
        }
      >
        <div ref={ref} className={cn("mx-auto w-full max-w-[452px] space-y-2 p-4", className)}>
          {renderPreview?.(cents, setAmount)}
          <AmountInput label={`${title} (${currency})`} value={localValue} onValueChange={setLocalValue} error={error}>
            {renderActions?.(setAmount)}
          </AmountInput>
        </div>
      </FullScreenDialog>
    );
  },
);
AmountEditor.displayName = "AmountEditor";

export const AmountPicker = forwardRef<HTMLDivElement, AmountPickerProps>(
  ({ open, ...props }, ref) =>
    open ? <AmountEditor key={props.value} {...props} ref={ref} /> : null,
);
AmountPicker.displayName = "AmountPicker";
